import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Download, 
  Sparkles, 
  FileText, 
  Copy, 
  FileEdit,
  Wand2,
  Star,
  Building,
  MapPin,
  RefreshCw,
  Save,
  CheckCircle,
  History,
  Edit,
  Eye,
  Calendar,
  Plus,
  Target
} from "lucide-react";
import { generateJobDescription } from '@/services/jobDescription';
import { exportJobDescriptionToPDF } from '@/utils/pdfExport';
import { DashboardService } from '@/services/dashboardService';

import { SubscriptionRequired } from "@/components/shared/SubscriptionRequired";
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useJobDescriptions } from './hooks/useJobDescriptions';

import { JobDescriptionValidation } from './ai-features/JobDescriptionValidation';
import { ValidationResult } from '@/services/jobDescriptionValidation';
import { 
  getAIAutoFillSuggestions, 
  getSmartFieldSuggestion,
  AutoFillSuggestions 
} from '@/services/aiAutoFill';
import { 
  AUDIENCE_TARGETS, 
  optimizeJobDescriptionForAudience, 
  analyzeJobDescriptionAudience,
  type AudienceOptimizationResult
} from '@/services/audienceTargeting';



// Local interface for job form data (replacing imported JobTemplateSample)
interface JobFormData {
  jobTitle: string;
  location: string;
  industry: string;
  experience: string;
  jobFunction?: string;
  skills: string;
  responsibilities: string;
  qualifications: string;
  benefits: string;
  companyMission: string;
  [key: string]: string | undefined;
}



// Form fields that will be used to collect information for the job description
const jobFormFields = [
  {
    id: 'jobTitle',
    label: 'Job Title',
    type: 'input',
    placeholder: 'e.g. Senior Frontend Developer',
    required: true,
    tooltip: 'The specific title for the position you\'re hiring for'
  },
  {
    id: 'location',
    label: 'Location',
    type: 'input',
    placeholder: 'e.g. San Francisco, CA (Remote/Hybrid/Onsite)',
    required: false,
    tooltip: 'Where the job is located, including remote options'
  },
  {
    id: 'industry',
    label: 'Industry',
    type: 'input',
    placeholder: 'e.g. Technology, Healthcare, Finance',
    required: false,
    tooltip: 'The specific industry your company operates in'
  },
  {
    id: 'experience',
    label: 'Years of Experience Required',
    type: 'input',
    placeholder: 'e.g. 3-5 years, Entry Level, Senior, etc.',
    required: false,
    tooltip: 'How many years of relevant experience candidates should have (e.g. "3-5 years", "Senior", "Entry Level")'
  },
  {
    id: 'skills',
    label: 'Key Skills & Technologies',
    type: 'textarea',
    placeholder: 'e.g. React, TypeScript, Node.js, AWS',
    required: true,
    tooltip: 'Technical skills, tools, languages, and technologies required'
  },
  {
    id: 'responsibilities',
    label: 'Main Responsibilities',
    type: 'textarea',
    placeholder: 'List the main duties and responsibilities for this role',
    required: true,
    tooltip: 'Key duties and tasks the candidate will be expected to perform'
  },
  {
    id: 'qualifications',
    label: 'Required Qualifications',
    type: 'textarea',
    placeholder: 'List education, certifications, and other qualifications',
    required: false,
    tooltip: 'Education, certifications, and other formal requirements'
  },
  {
    id: 'benefits',
    label: 'Benefits & Perks',
    type: 'textarea',
    placeholder: 'e.g. Health insurance, 401(k), flexible work hours',
    required: false,
    tooltip: 'Company benefits and perks that would attract candidates'
  },
  {
    id: 'companyMission',
    label: 'Company Mission/Values',
    type: 'textarea',
    placeholder: 'Brief description of company mission and culture',
    required: false,
    tooltip: 'Your company\'s mission, vision, and cultural values'
  }
];

// Default empty form data
const emptyFormData: JobFormData = {
  jobTitle: '',
  location: '',
  industry: '',
  experience: '',
  jobFunction: '',
  skills: '',
  responsibilities: '',
  qualifications: '',
  benefits: '',
  companyMission: ''
};

// Helper function to map experience text to database enum values
const mapExperienceToLevel = (experienceText: string): string | undefined => {
  if (!experienceText) return undefined;
  
  const text = experienceText.toLowerCase();
  
  // Check for specific patterns
  if (text.includes('entry') || text.includes('0-1') || text.includes('0-2') || text.includes('graduate') || text.includes('junior')) {
    return 'Entry Level';
  }
  if (text.includes('associate') || text.includes('1-3') || text.includes('2-4')) {
    return 'Associate';
  }
  if (text.includes('mid') || text.includes('3-5') || text.includes('4-6') || text.includes('5-7')) {
    return 'Mid-Level';
  }
  if (text.includes('senior') || text.includes('5+') || text.includes('6+') || text.includes('7+') || text.includes('8+')) {
    return 'Senior';
  }
  if (text.includes('lead') || text.includes('principal') || text.includes('staff')) {
    return 'Lead';
  }
  if (text.includes('director') || text.includes('manager') || text.includes('head')) {
    return 'Director';
  }
  if (text.includes('executive') || text.includes('vp') || text.includes('cto') || text.includes('ceo')) {
    return 'Executive';
  }
  
  // Default fallback based on numbers
  const yearMatch = text.match(/(\d+)/);
  if (yearMatch) {
    const years = parseInt(yearMatch[1]);
    if (years <= 2) return 'Entry Level';
    if (years <= 4) return 'Associate';
    if (years <= 7) return 'Mid-Level';
    if (years <= 10) return 'Senior';
    return 'Lead';
  }
  
  return undefined; // Let database handle validation
};

// Helper function to convert database enum values back to user-friendly text
const mapLevelToExperienceText = (level: string): string => {
  if (!level) return '';
  
  const levelMap: Record<string, string> = {
    'Entry Level': '0-2 years',
    'Associate': '2-4 years',
    'Mid-Level': '4-7 years',
    'Senior': '7+ years',
    'Lead': '8+ years (Lead/Principal)',
    'Director': '10+ years (Director/Manager)',
    'Executive': '12+ years (Executive/VP/C-Level)'
  };
  
  return levelMap[level] || level;
};

export function JobDescriptionGenerator() {
  return (
    <SubscriptionRequired 
      requiredPlans={['enterprise']}
      title="Generate Job Description with Smart Technology"
      description="Create professional job descriptions with our Smart-powered tool."
      icon={FileEdit}
      featureList={[
        "Generate polished job descriptions in seconds",
        "AI-powered intelligent auto-fill assistance",
        "Customize tone and formatting to match your brand",
        "Export to PDF for easy sharing",
        "Create unlimited job descriptions",
        "Save time on recruiting and focus on finding great candidates"
      ]}
    >
      <JobDescriptionGeneratorContent />
    </SubscriptionRequired>
  );
}

function JobDescriptionGeneratorContent(): JSX.Element {
  const [activeTab, setActiveTab] = useState('generate');
  const [formData, setFormData] = useState<JobFormData>(emptyFormData);
  const [jobDescription, setJobDescription] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [savedJobId, setSavedJobId] = useState<string | null>(null);
  const [editingJobId, setEditingJobId] = useState<string | null>(null);
  const [autoFillSuggestions, setAutoFillSuggestions] = useState<AutoFillSuggestions | null>(null);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [showAutoFillSuggestions, setShowAutoFillSuggestions] = useState(false);
  
  // Organization data
  const [organizationName, setOrganizationName] = useState<string>('');

  // Audience targeting states
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>([]);
  const [audienceOptimization, setAudienceOptimization] = useState<AudienceOptimizationResult | null>(null);
  const [isOptimizingForAudience, setIsOptimizingForAudience] = useState(false);
  const [audienceAnalysis, setAudienceAnalysis] = useState<any>(null);

  // Hooks for data fetching
  const { jobDescriptions, isLoading: jobDescriptionsLoading } = useJobDescriptions();

  // Debounced AI auto-fill - will be defined after handleAutoFill

  // Load organization name on mount
  useEffect(() => {
    const loadOrganizationInfo = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError) {
          // Handle RLS policy errors gracefully
          if (profileError.code === '42501' || profileError.message?.includes('policy') || profileError.message?.includes('406')) {
            console.log('Profile access blocked by RLS - user likely in onboarding');
            return;
          }
          // For other errors, just log and continue
          console.error('Error fetching profile:', profileError);
          return;
        }

        if (profile?.organization_id) {
          const { data: organization } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', profile.organization_id)
            .maybeSingle();

          if (organization?.name) {
            setOrganizationName(organization.name);
          }
        }
      } catch (error) {
        console.error('Error loading organization info:', error);
      }
    };

    loadOrganizationInfo();
  }, []);

  // Load existing job description for editing
  const handleEditJobDescription = (jobDesc: any) => {
    // Populate form with existing data
    const templateData = jobDesc.template_data || {};
    setFormData({
      jobTitle: jobDesc.title,
      location: jobDesc.location || '',
      industry: jobDesc.industry || templateData.industry || '',
      experience: mapLevelToExperienceText(jobDesc.experience_level) || templateData.experience || '',
      jobFunction: templateData.jobFunction || '',
      skills: jobDesc.requirements?.join(', ') || templateData.skills || '',
      responsibilities: jobDesc.responsibilities?.join('\n') || templateData.responsibilities || '',
      qualifications: jobDesc.qualifications?.join('\n') || templateData.qualifications || '',
      benefits: jobDesc.benefits?.join('\n') || templateData.benefits || '',
      companyMission: templateData.companyMission || ''
    });
    
    setJobDescription(jobDesc.content);
    setEditingJobId(jobDesc.id);
    setSavedJobId(jobDesc.id);
    
    // Load audience targeting data if available
    if (jobDesc.audience_targets?.length > 0) {
      setSelectedAudiences(jobDesc.audience_targets);
    }
    if (jobDesc.audience_optimization) {
      setAudienceOptimization(jobDesc.audience_optimization);
    }
    
    setActiveTab('generate');
    toast.success('Job description loaded for editing');
  };

  // Handle PDF export from saved jobs
  const handleExportSavedJobToPDF = async (jobDesc: any) => {
    try {
      // Create job description data from saved record
      const jobDescriptionData = {
        jobTitle: jobDesc.title,
        company: jobDesc.company || organizationName || 'Your Organization',
        location: jobDesc.location || '',
        industry: jobDesc.industry || '',
        experience: jobDesc.experience_level || '',
        skills: jobDesc.requirements?.join(', ') || '',
        responsibilities: jobDesc.responsibilities?.join('\n') || '',
        qualifications: jobDesc.qualifications?.join('\n') || '',
        benefits: jobDesc.benefits?.join('\n') || '',
        companyMission: jobDesc.template_data?.companyMission || '',
        fullDescription: jobDesc.content
      };
      
      exportJobDescriptionToPDF(jobDescriptionData);
      
      // Track analytics
      await DashboardService.updateJobDescriptionAnalytics(jobDesc.id, 'pdf_download');
      
      toast.success('PDF export started');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Reset form to empty state
  const handleResetForm = () => {
    setFormData(emptyFormData);
    setFormErrors({});
    setJobDescription('');
    setSavedJobId(null);
    setEditingJobId(null);
    setAutoFillSuggestions(null);
    setShowAutoFillSuggestions(false);
  };

  const validateForm = () => {
    const errors: Record<string, boolean> = {};
    let isValid = true;
    
    jobFormFields.forEach(field => {
      if (field.required && (!formData[field.id] || !formData[field.id]?.trim())) {
        errors[field.id] = true;
        isValid = false;
      }
    });
    
    setFormErrors(errors);
    return isValid;
  };

  const handleGenerateDescription = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const generatedDescription = await generateJobDescription({
        ...formData,
        company: organizationName || 'Your Organization', // Use organization name
        tone: 'professional',
        includeEqualOpportunity: true,
        formatWithMarkdown: false,
      });
      setJobDescription(generatedDescription);
      setSavedJobId(null); // Reset saved state when new description is generated
      toast.success('Job description generated successfully!');
    } catch (error) {
      console.error('Error generating job description:', error);
      toast.error('Failed to generate job description. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Save job description to database
  const handleSaveJobDescription = async () => {
    if (!jobDescription) return;
    
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (profileError) {
        // Handle RLS policy errors gracefully
        if (profileError.code === '42501' || profileError.message?.includes('policy') || profileError.message?.includes('406')) {
          console.log('Profile access blocked by RLS - user likely in onboarding');
          toast.error('Please complete organization setup first');
          return;
        }
        console.error('Error fetching profile:', profileError);
        toast.error('Error accessing user profile. Please try again.');
        return;
      }

      if (!user || !profile?.organization_id) {
        toast.error('Authentication error. Please refresh and try again.');
        return;
      }

      // Extract requirements and benefits from the form data or generated content
      const requirements = formData.skills 
        ? formData.skills.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];
      const responsibilities = formData.responsibilities 
        ? formData.responsibilities.split('\n').map(s => s.trim()).filter(s => s.length > 0)
        : [];
      const qualifications = formData.qualifications 
        ? formData.qualifications.split('\n').map(s => s.trim()).filter(s => s.length > 0)
        : [];
      const benefits = formData.benefits 
        ? formData.benefits.split('\n').map(s => s.trim()).filter(s => s.length > 0)
        : [];

      // Create tags from industry, job function, and experience
      const tags = [
        formData.industry,
        formData.jobFunction,
        formData.experience,
        'ai_generated'
      ].filter(Boolean) as string[];

      const jobDescriptionData = {
        user_id: user.id,
        organization_id: profile.organization_id,
        title: formData.jobTitle,
        content: jobDescription,
        company: organizationName || 'Your Organization',
        location: formData.location,
        industry: formData.industry,
        experience_level: mapExperienceToLevel(formData.experience), // Map to enum value
        requirements,
        responsibilities,
        qualifications,
        benefits,
        tags,
        template_data: {
          ...formData,
          generatedAt: new Date().toISOString(),
          generationType: 'ai_generated'
        },
        generation_settings: {
          tone: 'professional',
          includeEqualOpportunity: true,
          formatWithMarkdown: false
        },
        auto_fill_suggestions: autoFillSuggestions || {},
        audience_targets: selectedAudiences,
        audience_optimization: audienceOptimization || {},
        status: 'draft',
        is_published: false
      };

      let savedJD;
      if (editingJobId) {
        // Update existing job description
        savedJD = await DashboardService.updateJobDescriptionGenerated(editingJobId, jobDescriptionData);
      } else {
        // Create new job description
        savedJD = await DashboardService.saveJobDescriptionGenerated(jobDescriptionData);
      }
      
      if (savedJD) {
        setSavedJobId(savedJD.id);
        toast.success('Job description saved successfully!');
        
        // Track feature usage in Supabase (fire and forget)
        try {
          const { UserActionsService } = await import('../../services/userActions');
          await UserActionsService.trackFeatureUsage('job-generator', {
            job_title: formData.jobTitle,
            company_name: organizationName,
            generated_at: new Date().toISOString()
          });
        } catch (error) {
          console.error('Error tracking job generator usage:', error);
        }
        
        // Emit event to update dashboard
        window.dispatchEvent(new CustomEvent('jobDescriptionSaved'));
      } else {
        toast.error('Failed to save job description. Please try again.');
      }
    } catch (error) {
      console.error('Error saving job description:', error);
      
      // Display specific error message if available
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to save job description. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToClipboard = async () => {
    if (!jobDescription) return;
    
    try {
      await navigator.clipboard.writeText(jobDescription);
      toast.success('Job description copied to clipboard!');
      
      // Track analytics if job is saved
      if (savedJobId) {
        await DashboardService.updateJobDescriptionAnalytics(savedJobId, 'copy');
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleExportToPDF = async () => {
    if (!jobDescription) return;
    
    try {
      // Parse the AI-generated content to extract all sections
      const parseJobDescription = (content: string) => {
        const lines = content.split('\n');
        const parsed: any = {
          jobTitle: formData.jobTitle,
          company: organizationName || 'Your Organization',
          location: formData.location,
        };
        
        let currentSection = '';
        let currentContent: string[] = [];
        
        for (const line of lines) {
          const trimmedLine = line.trim();
          
          // Check for section headers
          if (trimmedLine.includes('Company Overview:') || trimmedLine.includes('**Company Overview:**')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'companyOverview';
            currentContent = [];
          } else if (trimmedLine.includes('Key Responsibilities:') || trimmedLine.includes('**Key Responsibilities:**')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'responsibilities';
            currentContent = [];
          } else if (trimmedLine.includes('Required Skills') || trimmedLine.includes('**Required Skills')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'skills';
            currentContent = [];
          } else if (trimmedLine.includes('Qualifications:') || trimmedLine.includes('**Qualifications:**')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'qualifications';
            currentContent = [];
          } else if (trimmedLine.includes('Benefits') || trimmedLine.includes('**Benefits')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'benefits';
            currentContent = [];
          } else if (trimmedLine.includes('Equal Employment Opportunity') || trimmedLine.includes('**Equal Employment Opportunity')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'equalOpportunity';
            currentContent = [];
          } else if (trimmedLine.includes('Work Environment:') || trimmedLine.includes('**Work Environment:**')) {
            if (currentSection) {
              parsed[currentSection] = currentContent.join('\n').trim();
            }
            currentSection = 'workEnvironment';
            currentContent = [];
          } else if (trimmedLine && !trimmedLine.includes('**Job Title:') && !trimmedLine.includes('**Location:')) {
            // Add content to current section
            if (currentSection) {
              currentContent.push(line);
            }
          }
        }
        
        // Don't forget the last section
        if (currentSection && currentContent.length > 0) {
          parsed[currentSection] = currentContent.join('\n').trim();
        }
        
        return parsed;
      };
      
      const parsedContent = parseJobDescription(jobDescription);
      
      // Create a comprehensive job description object with all AI-generated content
      const jobDescriptionData = {
        ...formData,
        ...parsedContent,
        // Pass the raw content as well for fallback
        fullDescription: jobDescription
      };
      
      exportJobDescriptionToPDF(jobDescriptionData);
      
      // Track analytics if job is saved
      if (savedJobId) {
        await DashboardService.updateJobDescriptionAnalytics(savedJobId, 'pdf_download');
      }
      
      toast.success('PDF export started');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };



  // Handle validation completion
  const handleValidationComplete = (result: ValidationResult) => {
    // Auto-switch to validation tab if there are issues
    if (result.issues.length > 0 || result.overall_score < 80) {
      setActiveTab('validation');
    }
  };

  // AI Auto-Fill Handler
  const handleAutoFill = async (triggerField: string) => {
    if (!formData.jobTitle?.trim()) {
      toast.info('Please enter a job title first');
      return;
    }

    setIsAutoFilling(true);
    try {
      toast.info('🤖 Analyzing job title and generating suggestions...');
      
      // Use the current job title to get suggestions
      const suggestions = await getAIAutoFillSuggestions(formData, triggerField);
      setAutoFillSuggestions(suggestions);
      setShowAutoFillSuggestions(true);
      
      toast.success(`✨ Generated suggestions based on "${formData.jobTitle}"`);
    } catch (error) {
      console.error('Auto-fill error:', error);
      toast.error('Failed to generate AI suggestions. Please try again.');
    } finally {
      setIsAutoFilling(false);
    }
  };

  // Apply AI suggestions
  const applyAutoFillSuggestion = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    toast.success(`Applied AI suggestion for ${field}`);
  };

  // Apply all AI suggestions
  const applyAllAutoFillSuggestions = () => {
    if (!autoFillSuggestions) return;

    let appliedCount = 0;
    const newFormData = { ...formData };

    Object.entries(autoFillSuggestions).forEach(([field, value]) => {
      if (field !== 'confidence' && field !== 'reasoning' && value && typeof value === 'string') {
        // Only apply if the field is empty or very short
        const currentValue = newFormData[field as keyof typeof newFormData] as string;
        if (!currentValue || currentValue.trim().length < 10) {
          newFormData[field as keyof typeof newFormData] = value;
          appliedCount++;
        }
      }
    });

    setFormData(newFormData);
    setShowAutoFillSuggestions(false);
    toast.success(`✅ Applied ${appliedCount} AI suggestions to your job description!`);
  };

  // Smart field enhancement
  const enhanceFieldWithAI = async (fieldType: 'skills' | 'responsibilities' | 'qualifications' | 'benefits') => {
    if (!formData.jobTitle?.trim()) {
      toast.info('Please enter a job title first');
      return;
    }

    setIsAutoFilling(true);
    try {
      toast.info(`🤖 AI is generating enhanced ${fieldType}...`);
      
      const suggestion = await getSmartFieldSuggestion(fieldType, {
        jobTitle: formData.jobTitle,
        industry: formData.industry,
        experienceLevel: formData.experience,
        company: organizationName
      });

      setFormData(prev => ({ ...prev, [fieldType]: suggestion }));
      toast.success(`✨ Enhanced ${fieldType} with AI suggestions!`);
    } catch (error) {
      console.error(`Smart ${fieldType} enhancement error:`, error);
      toast.error(`Failed to enhance ${fieldType}. Please try again.`);
    } finally {
      setIsAutoFilling(false);
    }
  };



  // Handle field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Format time ago helper
  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Audience targeting functions
  const handleAudienceToggle = (audienceId: string) => {
    setSelectedAudiences(prev => 
      prev.includes(audienceId)
        ? prev.filter(id => id !== audienceId)
        : [...prev, audienceId]
    );
  };

  const optimizeForAudience = async () => {
    if (!jobDescription.trim() || selectedAudiences.length === 0) {
      toast.error('Please generate a job description and select target audiences first');
      return;
    }

    setIsOptimizingForAudience(true);
    try {
      const result = await optimizeJobDescriptionForAudience({
        originalJobDescription: jobDescription,
        targetAudiences: selectedAudiences,
        jobTitle: formData.jobTitle,
        company: organizationName || 'Your Organization',
        industry: formData.industry,
        experienceLevel: formData.experience
      });

      setAudienceOptimization(result);
      setJobDescription(result.optimizedJobDescription);
      
      // If we have a saved job description, update it with the audience optimization
      if (savedJobId) {
        const appealScores: Record<string, number> = {};
        selectedAudiences.forEach(audience => {
          appealScores[audience] = result.audienceScore || 0;
        });

        await DashboardService.saveAudienceOptimization(
          savedJobId,
          selectedAudiences,
          result,
          appealScores
        );

        // Also update the main job description with optimization data
        await DashboardService.updateJobDescriptionGenerated(savedJobId, {
          content: result.optimizedJobDescription,
          audience_targets: selectedAudiences,
          audience_optimization: result,
          inclusivity_score: result.inclusivityScore,
          audience_appeal_score: result.audienceScore
        });

        // Trigger update event
        window.dispatchEvent(new CustomEvent('jobDescriptionUpdated'));
      }
      
      toast.success(`Job description optimized for ${selectedAudiences.length} audience(s)!`);
    } catch (error) {
      console.error('Audience optimization error:', error);
      toast.error('Failed to optimize for target audience. Please try again.');
    } finally {
      setIsOptimizingForAudience(false);
    }
  };

  const analyzeCurrentAudience = async () => {
    if (!jobDescription.trim()) {
      toast.error('Please generate a job description first');
      return;
    }

    try {
      const analysis = await analyzeJobDescriptionAudience(jobDescription, formData.jobTitle);
      setAudienceAnalysis(analysis);
    } catch (error) {
      console.error('Audience analysis error:', error);
      toast.error('Failed to analyze audience appeal. Please try again.');
    }
  };



  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3">
          <div className="h-12 w-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <Wand2 className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Job Description Generator</h1>
            <p className="text-slate-600">Create professional job descriptions with Smart assistance</p>
          </div>
        </div>
      </div>

      {/* Tab Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="bg-white rounded-xl p-2 shadow-sm border border-slate-200">
          <TabsList className="grid w-full grid-cols-3 bg-transparent gap-1">
            <TabsTrigger 
              value="generate" 
              className="flex items-center gap-2 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200"
            >
              <Plus className="h-4 w-4" />
              <span className="font-medium">Generate New</span>
            </TabsTrigger>
            <TabsTrigger 
              value="validation" 
              className="flex items-center gap-2 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200"
            >
              <Target className="h-4 w-4" />
              <span className="font-medium">Validation</span>
            </TabsTrigger>
            <TabsTrigger 
              value="previous" 
              className="flex items-center gap-2 px-4 py-3 rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white transition-all duration-200"
            >
              <History className="h-4 w-4" />
              <span className="font-medium">Previous Jobs</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Generate New Tab */}
        <TabsContent value="generate" className="space-y-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Section - Move all existing form content here */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader className="p-6">
                <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-3">
                  <FileText className="h-5 w-5 text-slate-600" />
                  {editingJobId ? 'Edit Job Details' : 'Job Details'}
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {editingJobId ? 'Modify the job information and regenerate' : 'Fill in the job information to generate a professional description'}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-6">
                {/* AI Assistant */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-50/80 rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 bg-gradient-to-r rom-slate-900 to-slate-800 rounded-xl flex items-center justify-center">
                       
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleAutoFill('manual')}
                        disabled={isAutoFilling || !formData.jobTitle?.trim()}
                        className="text-slate-700 hover:text-slate-900 border-slate-300 hover:bg-slate-100 bg-white"
                      >
                        {isAutoFilling ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-slate-600 border-t-transparent mr-1.5" />
                            Working...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3 w-3 mr-1.5" />
                            Get Suggestions
                          </>
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleResetForm}
                        className="text-slate-600 hover:text-slate-700 border-slate-300 hover:bg-slate-100 bg-white"
                      >
                        <RefreshCw className="h-3 w-3 mr-1.5" />
                        Reset
                      </Button>
                    </div>
                  </div>
                  
                  {/* AI Suggestions Display */}
                  {showAutoFillSuggestions && autoFillSuggestions && (
                    <div className="mt-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-slate-900">
                            Suggestions Ready for "{formData.jobTitle}"
                          </span>
                          <div className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium">
                            {autoFillSuggestions.confidence}%
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={applyAllAutoFillSuggestions}
                            className="bg-slate-900 hover:bg-slate-800 text-white"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Apply All
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowAutoFillSuggestions(false)}
                            className="border-slate-300 text-slate-600 hover:bg-slate-50"
                          >
                            Dismiss
                          </Button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {Object.entries(autoFillSuggestions).map(([field, value]) => {
                          if (field === 'confidence' || field === 'reasoning' || !value || typeof value !== 'string') return null;
                          
                          const currentValue = formData[field as keyof typeof formData] as string;
                          const shouldShow = !currentValue || currentValue.trim().length < 10;
                          
                          if (!shouldShow) return null;
                          
                          return (
                            <div key={field} className="bg-slate-50 rounded-lg p-3 border border-slate-200 hover:border-slate-300 transition-all group">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-800 capitalize">
                                  {field.replace(/([A-Z])/g, ' $1').trim()}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => applyAutoFillSuggestion(field, value)}
                                  className="text-xs border-slate-300 text-slate-600 hover:bg-white hover:border-slate-400 opacity-80 group-hover:opacity-100 transition-all"
                                >
                                  Apply
                                </Button>
                              </div>
                              <p className="text-xs text-slate-600 leading-relaxed line-clamp-2">{value}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Quick Enhancement */}
                  <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-200">
                    {(['skills', 'responsibilities', 'qualifications', 'benefits'] as const).map((fieldType) => (
                      <Button
                        key={fieldType}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => enhanceFieldWithAI(fieldType)}
                        disabled={isAutoFilling || !formData.jobTitle?.trim()}
                        className="text-xs border-slate-300 hover:bg-slate-100 hover:border-slate-400 flex items-center justify-center gap-1 py-2 bg-white transition-all disabled:opacity-50"
                      >
                        <Sparkles className="h-3 w-3 text-slate-500" />
                        <span className="capitalize">{fieldType}</span>
                      </Button>
                    ))}
                  </div>

                  {/* Status Message */}
                  {!formData.jobTitle?.trim() && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-blue-600" />
                        <p className="text-blue-800 text-sm">Enter a job title to activate AI suggestions</p>
                      </div>
                    </div>
                  )}

                  {editingJobId && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Edit className="h-4 w-4 text-amber-600" />
                        <p className="text-amber-800 text-sm">Editing mode: Modify and regenerate to update</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* All existing form fields go here - keeping the same structure */}
                {/* Basic Information */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="jobTitle" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500" />
                      Job Title *
                    </Label>
                    <Input
                      id="jobTitle"
                      value={formData.jobTitle}
                      onChange={(e) => handleFieldChange('jobTitle', e.target.value)}
                      placeholder="e.g., Senior Software Engineer"
                      className={`rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 ${formErrors.jobTitle ? 'border-red-300 focus:border-red-400' : ''}`}
                    />
                    {formErrors.jobTitle && (
                      <p className="text-red-500 text-xs">Job title is required</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <Building className="h-4 w-4 text-blue-500" />
                      Company
                    </Label>
                    <Input
                      id="company"
                      value={organizationName || 'Your Organization'}
                      readOnly
                      className="rounded-lg border-slate-200 bg-slate-50 text-slate-600 cursor-not-allowed"
                    />
                    <p className="text-xs text-slate-500">Company name is automatically set from your organization</p>
                  </div>
                </div>

                {/* Location and Industry */}
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="location" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-green-500" />
                        Location
                      </Label>
                      <Input
                        id="location"
                        value={formData.location}
                        onChange={(e) => handleFieldChange('location', e.target.value)}
                        placeholder="e.g., San Francisco, CA (Remote/Hybrid/Onsite)"
                        className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="industry" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                        <Building className="h-4 w-4 text-blue-500" />
                        Industry
                      </Label>
                      <Input
                        id="industry"
                        value={formData.industry}
                        onChange={(e) => handleFieldChange('industry', e.target.value)}
                        placeholder="e.g., Technology, Healthcare, Finance"
                        className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="jobFunction" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <Target className="h-4 w-4 text-purple-500" />
                      Job Function
                    </Label>
                    <Input
                      id="jobFunction"
                      value={formData.jobFunction}
                      onChange={(e) => handleFieldChange('jobFunction', e.target.value)}
                      placeholder="e.g., Engineering, Marketing, Sales"
                      className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                    />
                  </div>
                </div>

                {/* Experience */}
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="experience" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-orange-500" />
                      Years of Experience
                    </Label>
                    <Input
                      id="experience"
                      value={formData.experience}
                      onChange={(e) => handleFieldChange('experience', e.target.value)}
                      placeholder="e.g., 3-5 years"
                      className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                    />
                  </div>


                </div>

                {/* Key Skills */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-indigo-500" />
                    Key Skills *
                  </Label>
                  <Textarea
                    value={formData.skills}
                    onChange={(e) => handleFieldChange('skills', e.target.value)}
                    placeholder="List key skills, technologies, and competencies required for this role..."
                    className={`rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 min-h-[90px] resize-none ${formErrors.skills ? 'border-red-300 focus:border-red-400' : ''}`}
                  />
                  {formErrors.skills && (
                    <p className="text-red-500 text-xs flex items-center gap-1">
                      <span>Key skills are required</span>
                    </p>
                  )}
                </div>

                {/* Detailed Information */}
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="responsibilities" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-emerald-500" />
                      Key Responsibilities *
                    </Label>
                    <Textarea
                      id="responsibilities"
                      value={formData.responsibilities}
                      onChange={(e) => handleFieldChange('responsibilities', e.target.value)}
                      placeholder="Describe the main responsibilities and duties for this role..."
                      className={`rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 min-h-[110px] resize-none ${formErrors.responsibilities ? 'border-red-300 focus:border-red-400' : ''}`}
                    />
                    {formErrors.responsibilities && (
                      <p className="text-red-500 text-xs flex items-center gap-1">
                        <span>Key responsibilities are required</span>
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="qualifications" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-teal-500" />
                      Required Qualifications
                    </Label>
                    <Textarea
                      id="qualifications"
                      value={formData.qualifications}
                      onChange={(e) => handleFieldChange('qualifications', e.target.value)}
                      placeholder="List education, certifications, and other qualifications..."
                      className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 min-h-[110px] resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="benefits" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <Star className="h-4 w-4 text-rose-500" />
                      Benefits & Perks
                    </Label>
                    <Textarea
                      id="benefits"
                      value={formData.benefits}
                      onChange={(e) => handleFieldChange('benefits', e.target.value)}
                      placeholder="Describe benefits and perks (AI will enhance if left empty)..."
                      className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 min-h-[90px] resize-none"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyMission" className="text-sm font-medium text-slate-900 flex items-center gap-2">
                      <Building className="h-4 w-4 text-cyan-500" />
                      Company Mission/Values
                    </Label>
                    <Textarea
                      id="companyMission"
                      value={formData.companyMission}
                      onChange={(e) => handleFieldChange('companyMission', e.target.value)}
                      placeholder="Brief description of company mission and culture..."
                      className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400 min-h-[90px] resize-none"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="border-t border-slate-200 pt-6 space-y-3">
                  <Button 
                    onClick={handleGenerateDescription}
                    disabled={!formData.jobTitle || !formData.skills || !formData.responsibilities || loading}
                    className="w-full bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 text-white font-semibold py-3.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                        <span>Generating with AI...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        <span>{editingJobId ? 'Regenerate Description' : 'Generate Job Description'}</span>
                      </>
                    )}
                  </Button>
                  
                  {(formData.jobTitle && formData.skills && formData.responsibilities) && (
                    <Button 
                      onClick={() => setActiveTab('validation')}
                      variant="outline"
                      className="w-full border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 py-3 rounded-xl transition-all duration-200"
                    >
                      <Target className="h-4 w-4 mr-2" />
                      <span>Validate Job Requirements</span>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Output Section - Move existing output content here */}
            <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
              <CardHeader className="p-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-3">
                    <Wand2 className="h-5 w-5 text-slate-600" />
                    Generated Description
                    {savedJobId && (
                      <div className="flex items-center gap-1 text-green-600 text-sm">
                        <CheckCircle className="h-4 w-4" />
                        <span className="font-normal">Saved</span>
                      </div>
                    )}
                  </CardTitle>
                  {jobDescription && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveJobDescription}
                        disabled={saving || !!savedJobId}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-2 border-slate-600 border-t-transparent mr-1" />
                            Saving...
                          </>
                        ) : savedJobId ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-1 text-green-600" />
                            Saved
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-1" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCopyToClipboard}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleExportToPDF}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="p-6 pt-0 space-y-6">
                {jobDescription ? (
                  <>
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed bg-slate-50 p-6 rounded-lg border border-slate-200">
                        {jobDescription}
                      </pre>
                    </div>

                    {/* Audience Targeting Section */}
                    <div className="border-t border-slate-200 pt-6">
                      <div className="bg-gradient-to-r from-slate-50 to-slate-50/80 rounded-xl border border-slate-200 p-6 space-y-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl flex items-center justify-center">
                              <Target className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">Audience Targeting</h3>
                              <p className="text-sm text-slate-600">Optimize your job description for specific audiences</p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={analyzeCurrentAudience}
                            className="text-slate-700 hover:text-slate-900 border-slate-300 hover:bg-slate-100 bg-white"
                          >
                            <Target className="h-4 w-4 mr-2" />
                            Analyze Appeal
                          </Button>
                        </div>

                        {/* Audience Selection */}
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium text-slate-900">
                              Select Target Audiences ({selectedAudiences.length} selected)
                            </Label>
                          </div>
                          
                                                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                             {AUDIENCE_TARGETS.map((audience) => {
                               const isSelected = selectedAudiences.includes(audience.id);
                              
                              return (
                                <button
                                  key={audience.id}
                                  onClick={() => handleAudienceToggle(audience.id)}
                                  className={`p-3 rounded-lg border-2 transition-all duration-200 text-left hover:scale-105 ${
                                    isSelected
                                      ? 'border-slate-400 bg-slate-100 shadow-md'
                                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                                      isSelected ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
                                    }`}>
                                      <span className="text-sm">{audience.icon}</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-sm text-slate-900 truncate">
                                        {audience.name}
                                      </div>
                                      <div className="text-xs text-slate-600 truncate">
                                        {audience.description}
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <CheckCircle className="h-4 w-4 text-slate-700 flex-shrink-0" />
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>

                          {selectedAudiences.length > 0 && (
                            <div className="flex gap-3 pt-2">
                              <Button
                                onClick={optimizeForAudience}
                                disabled={isOptimizingForAudience}
                                className="bg-slate-900 hover:bg-slate-800 text-white"
                              >
                                {isOptimizingForAudience ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                                    Optimizing...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    Optimize for Selected Audiences
                                  </>
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => setSelectedAudiences([])}
                                className="text-slate-600 border-slate-300"
                              >
                                Clear Selection
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Optimization Results */}
                        {audienceOptimization && (
                          <div className="bg-white rounded-xl border-2 border-green-200 p-6 space-y-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 bg-green-500 rounded-xl flex items-center justify-center">
                                <CheckCircle className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-green-900">Optimization Complete!</h4>
                                <p className="text-sm text-green-700">{audienceOptimization.summary}</p>
                              </div>
                            </div>
                            


                            {audienceOptimization.changes.length > 0 && (
                              <div className="space-y-3">
                                <h5 className="font-medium text-slate-900">Key Changes Made:</h5>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {audienceOptimization.changes.slice(0, 5).map((change, index) => (
                                    <div key={index} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                                      <div className="flex items-start gap-2">
                                        <Badge variant="outline" className="text-xs">
                                          {change.audienceTarget}
                                        </Badge>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-sm font-medium text-slate-900">{change.section}</div>
                                          <div className="text-xs text-slate-600 mt-1">{change.reason}</div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {audienceOptimization.changes.length > 5 && (
                                    <div className="text-xs text-slate-500 text-center py-2">
                                      +{audienceOptimization.changes.length - 5} more changes
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {audienceOptimization.recommendations && audienceOptimization.recommendations.length > 0 && (
                              <div className="space-y-2">
                                <h5 className="font-medium text-slate-900">Recommendations:</h5>
                                <ul className="space-y-1">
                                  {audienceOptimization.recommendations.slice(0, 3).map((rec, index) => (
                                    <li key={index} className="text-sm text-slate-700 flex items-start gap-2">
                                      <span className="text-slate-500 mt-1 flex-shrink-0">•</span>
                                      <span className="flex-1">{rec || 'No specific recommendation available'}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Audience Analysis Results */}
                        {audienceAnalysis && (
                          <div className="bg-white rounded-xl border-2 border-slate-200 p-6 space-y-4">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 bg-slate-900 rounded-xl flex items-center justify-center">
                                <Target className="h-5 w-5 text-white" />
                              </div>
                              <div>
                                <h4 className="font-semibold text-slate-900">Audience Appeal Analysis</h4>
                                <p className="text-sm text-slate-600">How your job description appeals to different audiences</p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                              {audienceAnalysis.currentAudienceAppeal?.map((appeal: any, index: number) => (
                                <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-medium text-sm text-slate-900">{appeal.audienceName}</span>
                                    <Badge 
                                      variant={appeal.appealScore >= 70 ? "default" : appeal.appealScore >= 50 ? "secondary" : "destructive"}
                                      className="text-xs"
                                    >
                                      {appeal.appealScore}%
                                    </Badge>
                                  </div>
                                  {appeal.strengths?.length > 0 && (
                                    <div className="text-xs text-green-700 mb-1">
                                      ✓ {appeal.strengths[0]}
                                    </div>
                                  )}
                                  {appeal.issues?.length > 0 && (
                                    <div className="text-xs text-red-600">
                                      ⚠ {appeal.issues[0]}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-slate-900">Overall Inclusivity</span>
                                <Badge 
                                  variant={(audienceAnalysis.overallInclusivity || 0) >= 80 ? "default" : "secondary"}
                                  className="bg-slate-100 text-slate-800"
                                >
                                  {audienceAnalysis.overallInclusivity || 0}%
                                </Badge>
                              </div>
                              {audienceAnalysis.recommendations?.length > 0 && (
                                <div className="space-y-1">
                                  {audienceAnalysis.recommendations.slice(0, 2).map((rec: string, index: number) => (
                                    <div key={index} className="text-xs text-slate-700 flex items-start gap-2">
                                      <span className="text-slate-500 mt-0.5 flex-shrink-0">•</span>
                                      <span className="flex-1">{rec || 'No specific recommendation available'}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Wand2 className="h-16 w-16 text-slate-300 mb-4" />
                    <p className="text-slate-600 font-medium mb-2">Ready to Generate</p>
                    <p className="text-slate-500 text-sm">Fill in the job details and let our AI assist you, then click "Generate Job Description" to create a professional job posting</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Validation Tab */}
        <TabsContent value="validation" className="space-y-0">
          <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
            <CardHeader className="p-6">
              <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-3">
                <Target className="h-5 w-5 text-slate-600" />
                Job Description Validation
              </CardTitle>
              <CardDescription className="text-slate-600">
                AI-powered analysis to ensure your job description has realistic expectations and market alignment
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {formData.jobTitle && formData.skills && formData.responsibilities ? (
                <JobDescriptionValidation
                  jobData={{
                    ...formData,
                    company: organizationName || 'Your Organization',
                    content: jobDescription
                  }}
                  onValidationComplete={handleValidationComplete}
                />
              ) : (
                <div className="text-center py-12">
                  <Target className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">Ready to Validate</h3>
                  <p className="text-slate-500 mb-6">
                    Complete the essential job details in the "Generate New" tab to validate your job description
                  </p>
                  <div className="text-center space-y-2 mb-6">
                    <p className="text-sm text-slate-500">✅ Realistic expectations check</p>
                    <p className="text-sm text-slate-500">🎯 Skills-to-experience alignment</p>
                    <p className="text-sm text-slate-500">📊 Market insights and recommendations</p>
                    <p className="text-sm text-slate-500">🚫 Bias and exclusionary language detection</p>
                  </div>
                  <Button onClick={() => setActiveTab('generate')} variant="outline">
                    Complete Job Details
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Previous Jobs Tab */}
        <TabsContent value="previous" className="space-y-0">
          <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
            <CardHeader className="p-6">
              <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-3">
                <History className="h-5 w-5 text-slate-600" />
                Previous Job Descriptions
              </CardTitle>
              <CardDescription className="text-slate-600">
                View, edit, and manage your previously generated job descriptions
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              {jobDescriptionsLoading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="p-4 border border-slate-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="h-4 bg-slate-200 rounded animate-pulse w-1/3"></div>
                          <div className="h-3 bg-slate-100 rounded animate-pulse w-1/2"></div>
                        </div>
                        <div className="flex gap-2">
                          <div className="h-8 w-16 bg-slate-200 rounded animate-pulse"></div>
                          <div className="h-8 w-16 bg-slate-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : jobDescriptions.length === 0 ? (
                <div className="text-center py-12">
                  <FileEdit className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No job descriptions yet</h3>
                  <p className="text-slate-500 mb-6">Start by generating your first job description</p>
                  <Button onClick={() => setActiveTab('generate')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Generate New Job Description
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobDescriptions.map((jobDesc) => (
                    <div key={jobDesc.id} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-slate-900 truncate">{jobDesc.title}</h3>
                            {jobDesc.is_published && (
                              <Badge variant="default" className="bg-green-100 text-green-800">
                                <Eye className="h-3 w-3 mr-1" />
                                Published
                              </Badge>
                            )}
                            {jobDesc.tags?.map((tag: string) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-600">
                            {jobDesc.company && (
                              <span className="flex items-center gap-1">
                                <Building className="h-3 w-3" />
                                {jobDesc.company}
                              </span>
                            )}
                            {jobDesc.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {jobDesc.location}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatTimeAgo(jobDesc.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditJobDescription(jobDesc)}
                            className="text-slate-600 hover:text-slate-900"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportSavedJobToPDF(jobDesc)}
                            className="text-slate-600 hover:text-slate-900"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          {/* Delete functionality removed as per requirement */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 